/**
 * One place that knows who is signed in, on which device, with what plan.
 *
 * The old app re-fetched user + subscription + device in every component's
 * useEffect, which is what produced the HTTP 429 storms. Here it is loaded
 * once and shared, and refresh() is called explicitly after a mutation.
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth, profile as profileApi, subscription as subApi, devices, affiliate } from './api.js';
import { deviceKey, describeDeviceAsync } from './device.js';
import { registerPush } from './native.js';
import { configure as configurePurchases } from './purchases.js';

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [state, setState] = useState({
    status: 'loading', user: null, profile: null, subscription: null, device: null,
  });

  const load = useCallback(async () => {
    const user = await auth.user();
    if (!user) {
      setState({ status: 'anonymous', user: null, profile: null, subscription: null, device: null });
      return;
    }
    // Invited with a code (link or typed at sign-up)? Claim it now that there
    // is a session — this also covers email-confirmation and OAuth sign-ups.
    // The server refuses anything but a fresh account, so a stale code is harmless.
    const code = affiliate.pending();
    if (code) {
      affiliate.forget();
      await affiliate.claim(code).catch((e) => console.warn('invite code not applied:', e.message));
    }

    const desc = await describeDeviceAsync();
    const key = await deviceKey();
    const [prof, sub, device] = await Promise.all([
      profileApi.get(),
      subApi.current(),
      devices.register({
        deviceKey: key, label: desc.label, kind: desc.kind,
        platform: desc.platform, userAgent: navigator.userAgent,
      }),
    ]);
    // Remotely deactivated? Sign this device out before showing anything.
    if (device?.force_logout) {
      const forced = await devices.consumeForcedLogout(device.id).catch(() => false);
      if (forced) {
        await auth.signOut();
        setState({
          status: 'anonymous', user: null, profile: null, subscription: null,
          device: null, notice: 'This device was deactivated from another device. Sign in again to continue.',
        });
        return;
      }
    }

    setState({ status: 'ready', user, profile: prof, subscription: sub, device });

    // Push registration and store binding are best-effort and must never block sign-in.
    registerPush(device.id, devices).catch((e) => console.warn('push unavailable', e));
    configurePurchases(user.id, user.email).catch((e) => console.warn('purchases unavailable', e));
  }, []);

  useEffect(() => {
    load().catch((e) => {
      console.error('session load failed', e);
      setState((s) => ({ ...s, status: 'anonymous' }));
    });
    const sub = auth.onChange(() => load().catch(console.error));
    return () => sub?.unsubscribe();
  }, [load]);

  const value = {
    ...state,
    refresh: load,
    hasActiveSubscription: subApi.isActive(state.subscription),
    isAdmin: state.profile?.role === 'admin',
    tokens: state.profile?.token_balance ?? 0,
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
